/**
 * N2c (T4, SC-N2b-09): the card rules both grids' automatic anatomy shares
 * below md (`data-grid/components/card.tsx`, `data-grid-light`'s cards).
 *
 * A card's title names the record, so it is never an id, a number or a date
 * while the grid shows a column that holds text -- and a column's template
 * does not say which it holds (a plain column can hold a registration number,
 * a function template can draw a patient's name), so its values do: most of
 * the first rows' values, read the way the card reads them.
 */

/** How many rows a column's values are read from. */
const SAMPLE = 20;
// digits and the punctuation of numbers, phones, codes and formatted dates
const NUMBER_LIKE = /^[\d\s\-+.,/:#()]+$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A value that names something: a string that is not a number, an id or a date. */
const isTextValue = (value: unknown) => {
  if (typeof value !== 'string') {
    return false;
  }
  const text = value.trim();
  return text !== '' && !NUMBER_LIKE.test(text) && !ISO_DATE.test(text) && !GUID.test(text);
};

const isEmpty = (value: unknown) => value === null || value === undefined || value === '';

/**
 * A cell's own value is set: not null, undefined, '' or an empty list. A card
 * draws a face row (N3, D-131) and its details toggle (N1) only for one.
 */
export const hasCellValue = (value: unknown) =>
  !(isEmpty(value) || (Array.isArray(value) && !value.length));

/**
 * A column holds text when most of its first rows' values (the ones that are
 * set) are text: a registration number column with one letter code among its
 * numbers still holds numbers.
 */
export const isTextValued = (rows: readonly unknown[] | undefined, read: (row: any) => unknown) => {
  const values = (rows ?? [])
    .slice(0, SAMPLE)
    .map(read)
    .filter((value) => !isEmpty(value));
  const text = values.filter(isTextValue).length;
  return text > 0 && text * 2 >= values.length;
};

const ROW_ID_TITLES = ['row-index', '#', 'id'];

/**
 * The row's index / id column ("#"): a card's position in the list, not a fact
 * about the record -- behind the card's details, never on its face. A titled
 * column is one by its title (`row-index`, `#`, `id`): a config may key another
 * value by `id` (the active patients' plan end date is `field: 'id'` under its
 * own title). An untitled one -- a light grid's column -- by its field,
 * `rowIndex`.
 */
export const isRowIdColumn = (field: unknown, title?: unknown) =>
  typeof title === 'string' && title.trim() !== ''
    ? ROW_ID_TITLES.includes(title.trim())
    : String(field) === 'rowIndex';
